import { useCallback } from 'react'
import { buildRefinementPayload } from './buildRefinementMessage'
import { resolveSendFiles } from './resolveSendFiles'

export function useAiAskBarSendActions({
  onSend,
  sendDisabled,
  isDictating,
  composerEditorRef,
  connectContextRef,
  attachmentsRef,
  setComposerPlainText,
  setAttachments,
  uploads,
  chat,
  model,
  refinement,
  clearRefinement,
}) {
  const handleSend = useCallback(async () => {
    const trimmed = composerEditorRef.current?.getPlainText()?.trim() ?? ''
    const hasBody = trimmed.length > 0 || attachmentsRef.current.length > 0
    if (!onSend || sendDisabled || !hasBody) return
    const refinementPayload = buildRefinementPayload(refinement)
    const ctx = connectContextRef.current
    connectContextRef.current = null
    // Snapshot then clear the composer immediately so the input feels responsive
    // and a second Enter mid-upload can't re-send the same content.
    const snapshot = attachmentsRef.current
    snapshot.forEach((a) => {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
    })
    composerEditorRef.current?.clearDocument()
    setComposerPlainText('')
    setAttachments([])
    clearRefinement?.()
    // Wait for in-flight uploads so each attachment ships with its documentId.
    const files = await resolveSendFiles(snapshot, uploads)
    // A chat minted by an attachment: send adopts it rather than minting a
    // second one, and is where it finally becomes the selected thread.
    const pending = chat?.take?.()
    // No researchMode: the backend defaults to "auto" so the agent decides for
    // itself whether a turn needs the web.
    onSend({
      text: trimmed,
      files,
      model,
      ...(refinementPayload ? { refinement: refinementPayload } : {}),
      ...(pending ? { chatId: pending.id, isProvisionalHomeThread: true } : {}),
      ...(ctx ? { connectContext: ctx } : {}),
    })
  }, [attachmentsRef, chat, clearRefinement, composerEditorRef, connectContextRef, model, onSend, refinement, sendDisabled, setAttachments, setComposerPlainText, uploads])

  const handleComposerEnter = useCallback(() => {
    if (isDictating || sendDisabled) return
    handleSend()
  }, [handleSend, isDictating, sendDisabled])

  return { handleSend, handleComposerEnter }
}
